import { Injectable } from '@angular/core';
import { downloadBlob } from '../../../utils/blob-viewer.util';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, delay, map, tap, withLatestFrom, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { AgreementsService } from '@services/agreements.service';
import { ErrorDialogService } from '@services/error-dialog.service';
import { AgreementActions } from '../../actions/agreement/agreement.actions';
import { selectSearchYear, selectUploadedDocuments } from '@store/selectors/agreement/agreement.selectors';
import { selectUserOrgId } from '@store/selectors/user/user.selectors';
import { DocusignActions } from '@store/actions/docusign/docusign.actions';
import { UploadDocumentResponse } from '@models/upload-document-response';

@Injectable()
export class AgreementEffects {
  getAgreements$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        AgreementActions.refreshButtonPressed,
        AgreementActions.searchYearChanged,
        DocusignActions.sendDocusignEnvelopeSuccess,
      ),
      concatLatestFrom(() => [
        this.store.select(selectSearchYear),
        this.store.select(selectUserOrgId)
      ]),
      switchMap(([,year, orgId]) => {
          return this.agreementsService.getAgreements({ year: year, orgId: orgId}).pipe(
          map(data => AgreementActions.getAgreementsSuccess({ agreements: data })),
          catchError(error => of(AgreementActions.getAgreementsFailure({ error })))
        );
      })
    )
  });

uploadDocument$ = createEffect(() => {
  return this.actions$.pipe(
    ofType(AgreementActions.uploadDocument),
    withLatestFrom(this.store.select(selectUserOrgId)), 
    switchMap(([{ file, documentTypeId }, organizationId]) => {
      return this.agreementsService.uploadDocument(file, organizationId, documentTypeId).pipe(
        map((response: UploadDocumentResponse) => {
          return AgreementActions.documentUploadSuccess({ response });
        }),
        catchError(error => of(AgreementActions.documentUploadFailure({ 
          error: error.message || 'Document upload failed' 
        })))
      );
    })
  );
});

  openEmbedSigning$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AgreementActions.openInDocusignPressed),
      switchMap((action) => {

        return this.agreementsService.getRecipientView(action.envelopeId).pipe(

         map((data) => {
           return DocusignActions.getSigningUrlSuccess({ url: data.returnURL})
         }),
          catchError((error) => of(DocusignActions.getSigningUrlFailure({ error })))
        )
      })

    )
  });


loadDocuments$ = createEffect(() => {
  return this.actions$.pipe(
    ofType(AgreementActions.loadDocuments),
    withLatestFrom(this.store.select(selectUserOrgId)),
    switchMap(([action, orgId]) => {
      return this.agreementsService.getUploadedDocuments(orgId).pipe(
        map(documents => AgreementActions.loadDocumentsSuccess({ documents })),
        catchError(error => of(AgreementActions.loadDocumentsFailure({ error: error.message || 'Failed to load documents' })))
      );
    })
  );
});

downloadDocument$ = createEffect(() => {
  return this.actions$.pipe(
    ofType(AgreementActions.downloadDocument),
    withLatestFrom(
      this.store.select(selectUserOrgId),
      this.store.select(selectUploadedDocuments)
    ),
    switchMap(([{ documentId }, orgId, documents]) => {
      const foundDocument = documents.find(doc => doc.documentId == documentId);
      const filename = foundDocument?.fileName || `document_${documentId}.pdf`;
      return this.agreementsService.downloadDocument(documentId, orgId).pipe(
        map((blob: Blob) => {
          downloadBlob(blob, filename);

          return AgreementActions.downloadDocumentSuccess();
        }),
        catchError(error => of(AgreementActions.downloadDocumentFailure({
          error: error.message || 'Document download failed'
        })))
      );
    })
  );
});

downloadOrgAgreement$ = createEffect(() => {
  return this.actions$.pipe(
    ofType(AgreementActions.downloadOrgAgreement),
    switchMap(({ envelopeId }) => {
      return this.agreementsService.downloadOrgAgreement(envelopeId).pipe(
        map((blob: Blob) => {
          downloadBlob(blob, `OrgAgreement_${envelopeId}.pdf`);

          return AgreementActions.downloadOrgAgreementSuccess();
        }),
        catchError(error => {
          const message = error.status === 404
            ? ''
            : error.message || 'Agreement download failed';
          return of(AgreementActions.downloadOrgAgreementFailure({ error: message }));
        })
      );
    })
  );
});

downloadOrgAgreementFailure$ = createEffect(() => {
  return this.actions$.pipe(
    ofType(AgreementActions.downloadOrgAgreementFailure),
    tap(({ error }) => {
      if (error) {
        this.errorDialogService.showError(error, 'Download Error').subscribe();
      }
    })
  );
}, { dispatch: false });

sendOrgAgreementWithEmail$ = createEffect(() => {
  return this.actions$.pipe(
    ofType(AgreementActions.sendOrgAgreementWithEmail),
    withLatestFrom(this.store.select(selectUserOrgId)),
    switchMap(([{ email, name }, orgId]) => {
      return this.agreementsService.makeEnvelope(orgId, email, name).pipe(
        switchMap(() => [
          AgreementActions.sendOrgAgreementWithEmailSuccess(),
          AgreementActions.refreshButtonPressed()
        ]),
        catchError(error => of(AgreementActions.sendOrgAgreementWithEmailFailure({
          error: error.message || 'Failed to send agreement'
        })))
      );
    })
  );
});

sendOrgAgreementWithEmailFailure$ = createEffect(() => {
  return this.actions$.pipe(
    ofType(AgreementActions.sendOrgAgreementWithEmailFailure),
    tap(({ error }) => {
      this.errorDialogService.showError(error, 'Send Agreement Error').subscribe();
    })
  );
}, { dispatch: false });

voidOrgAgreement$ = createEffect(() => {
  return this.actions$.pipe(
    ofType(AgreementActions.voidOrgAgreement),
    switchMap(({ envelopeId }) => {
      return this.agreementsService.voidOrgAgreement(envelopeId).pipe(
        switchMap(() => [
          AgreementActions.voidOrgAgreementSuccess(),
          AgreementActions.refreshButtonPressed()
        ]),
        catchError(error => of(AgreementActions.voidOrgAgreementFailure({
          error: error.message || 'Failed to void agreement'
        })))
      );
    })
  );
});

voidOrgAgreementFailure$ = createEffect(() => {
  return this.actions$.pipe(
    ofType(AgreementActions.voidOrgAgreementFailure),
    tap(({ error }) => {
      this.errorDialogService.showError(error, 'Void Error').subscribe();
    })
  );
}, { dispatch: false });

refreshAfterSigning$ = createEffect(() => {
  return this.actions$.pipe(
    ofType(DocusignActions.getSigningUrlSuccess),
    delay(1000),
    map(() => AgreementActions.refreshButtonPressed())
  );
});

  constructor(
    private actions$: Actions,
    private store: Store,
    private agreementsService: AgreementsService,
    private errorDialogService: ErrorDialogService
  ) {}
}
